<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BrandingSettingsAPI from 'dashboard/api/brandingSettings';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ThemeBuilder from './ThemeBuilder.vue';
import SidebarVisibility from '../account/components/SidebarVisibility.vue';
import { useStore } from 'dashboard/composables/store';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';

const { t } = useI18n();
const store = useStore();
const loading = ref(true);
const saving = ref(false);
const form = reactive({ installationName: '', brandName: '' });
const assets = reactive({ logo: '', logoDark: '', favicon: '' });
const files = reactive({ logo: null, logoDark: null, favicon: null });
const hiddenSidebarItems = ref([]);
const currentUser = computed(() => store.getters.getCurrentUser);
const accountId = computed(() => store.getters.getCurrentAccountId);
const permissions = computed(() =>
  getUserPermissions(currentUser.value, accountId.value)
);
const isAdministrator = computed(() =>
  currentUser.value?.accounts?.some(
    account =>
      Number(account.id) === Number(accountId.value) &&
      account.role === 'administrator'
  )
);
const hasPermission = permission =>
  isAdministrator.value ||
  permissions.value.includes('personalization_manage') ||
  permissions.value.includes(permission);
const canManageBranding = computed(() =>
  hasPermission('personalization_branding_manage')
);
const canManageSidebar = computed(() =>
  hasPermission('personalization_sidebar_manage')
);
const canManageThemes = computed(() =>
  hasPermission('personalization_theme_manage')
);

const loadSettings = async () => {
  try {
    const { data } = await BrandingSettingsAPI.get();
    form.installationName = data.installation_name || '';
    form.brandName = data.brand_name || '';
    assets.logo = data.logo || '';
    assets.logoDark = data.logo_dark || '';
    assets.favicon = data.favicon || '';
    hiddenSidebarItems.value = data.hidden_sidebar_items || [];
  } catch {
    useAlert(t('BRANDING_SETTINGS.ERROR'));
  } finally {
    loading.value = false;
  }
};

const selectFile = (field, event) => {
  files[field] = event.target.files?.[0] || null;
};

const save = async () => {
  saving.value = true;
  const payload = new FormData();
  if (canManageBranding.value) {
    payload.append('installation_name', form.installationName);
    payload.append('brand_name', form.brandName);
    if (files.logo) payload.append('logo', files.logo);
    if (files.logoDark) payload.append('logo_dark', files.logoDark);
    if (files.favicon) payload.append('favicon', files.favicon);
  }
  if (canManageSidebar.value) {
    payload.append(
      'hidden_sidebar_items',
      JSON.stringify(hiddenSidebarItems.value)
    );
  }
  try {
    const { data } = await BrandingSettingsAPI.update(payload);
    Object.assign(assets, {
      logo: data.logo,
      logoDark: data.logo_dark,
      favicon: data.favicon,
    });
    if (canManageBranding.value) {
      store.dispatch('globalConfig/updateBranding', {
        installationName: data.installation_name,
        brandName: data.brand_name,
        logo: data.logo,
        logoDark: data.logo_dark,
        logoThumbnail: data.favicon,
      });
      document.title = data.installation_name;
      document.querySelectorAll('link[rel="icon"]').forEach(link => {
        link.href = data.favicon;
      });
    }
    await store.dispatch('accounts/get', { silent: true });
    useAlert(t('BRANDING_SETTINGS.SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('BRANDING_SETTINGS.ERROR'));
  } finally {
    saving.value = false;
  }
};

onMounted(loadSettings);
</script>

<template>
  <SettingsLayout :is-loading="loading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('BRANDING_SETTINGS.TITLE')"
        :description="$t('BRANDING_SETTINGS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="max-w-4xl">
        <form
          v-if="canManageBranding || canManageSidebar"
          class="space-y-6"
          @submit.prevent="save"
        >
          <div v-if="canManageBranding" class="grid gap-5 sm:grid-cols-2">
            <label class="space-y-2 text-sm text-n-slate-12">
              <span>{{ $t('BRANDING_SETTINGS.INSTALLATION_NAME') }}</span>
              <input
                v-model="form.installationName"
                required
                class="w-full h-10 px-3 rounded-lg border border-n-weak bg-n-alpha-2"
              />
            </label>
            <label class="space-y-2 text-sm text-n-slate-12">
              <span>{{ $t('BRANDING_SETTINGS.BRAND_NAME') }}</span>
              <input
                v-model="form.brandName"
                required
                class="w-full h-10 px-3 rounded-lg border border-n-weak bg-n-alpha-2"
              />
            </label>
          </div>
          <div v-if="canManageBranding" class="grid gap-5 sm:grid-cols-3">
            <label
              v-for="field in ['logo', 'logoDark', 'favicon']"
              :key="field"
              class="space-y-3 text-sm text-n-slate-12"
            >
              <span>{{
                $t(
                  `BRANDING_SETTINGS.${field === 'logoDark' ? 'LOGO_DARK' : field.toUpperCase()}`
                )
              }}</span>
              <div
                class="flex h-24 items-center justify-center rounded-lg border border-n-weak bg-n-alpha-2 p-3"
              >
                <img
                  v-if="assets[field]"
                  :src="assets[field]"
                  class="max-h-full max-w-full object-contain"
                />
              </div>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon"
                class="block w-full text-xs"
                @change="selectFile(field, $event)"
              />
            </label>
          </div>
          <p v-if="canManageBranding" class="text-xs text-n-slate-10">
            {{ $t('BRANDING_SETTINGS.FILE_HINT') }}
          </p>
          <SidebarVisibility
            v-if="canManageSidebar"
            v-model="hiddenSidebarItems"
          />
          <Button
            type="submit"
            :is-loading="saving"
            :label="$t('BRANDING_SETTINGS.SAVE')"
          />
        </form>
        <ThemeBuilder v-if="canManageThemes" />
      </div>
    </template>
  </SettingsLayout>
</template>
